const REDACTED = "[REDACTED]";

const SENSITIVE_KEY_PATTERNS = [
  /authorization/i,
  /cookie/i,
  /set-cookie/i,
  /token/i,
  /secret/i,
  /password/i,
  /api[-_]?key/i,
  /session/i,
  /bearer/i,
  /client[-_]?secret/i,
  /private[-_]?key/i,
];

const RAW_ALLOWED_KEYS = new Set([
  "user_ip",
  "user_country",
  "user_browser",
  "user_device",
  "user_email",
  "user_id",
]);

const MAX_STRING_LENGTH = 2000;
const MAX_DEPTH = 6;

function isSensitiveKey(key: string): boolean {
  if (RAW_ALLOWED_KEYS.has(key.toLowerCase())) return false;
  return SENSITIVE_KEY_PATTERNS.some((pattern) => pattern.test(key));
}

function sanitizeString(value: string): string {
  if (value.length <= MAX_STRING_LENGTH) return value;
  return `${value.slice(0, MAX_STRING_LENGTH)}...[truncated]`;
}

export function sanitizeUnknown(value: unknown, depth = 0): unknown {
  if (depth > MAX_DEPTH) {
    return "[MaxDepth]";
  }

  if (value == null) return value;

  if (typeof value === "string") return sanitizeString(value);

  if (
    typeof value === "number" ||
    typeof value === "boolean" ||
    typeof value === "bigint"
  ) {
    return value;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (value instanceof Error) {
    return {
      name: value.name,
      message: sanitizeString(value.message),
      stack: value.stack ? sanitizeString(value.stack) : undefined,
    };
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeUnknown(item, depth + 1));
  }

  if (typeof Headers !== "undefined" && value instanceof Headers) {
    return sanitizeHeaders(value);
  }

  if (typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [key, inner] of Object.entries(
      value as Record<string, unknown>,
    )) {
      if (isSensitiveKey(key)) {
        out[key] = REDACTED;
      } else {
        out[key] = sanitizeUnknown(inner, depth + 1);
      }
    }
    return out;
  }

  return String(value);
}

export function sanitizeHeaders(
  headers: Headers | Record<string, string | string[] | undefined>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};

  if (typeof Headers !== "undefined" && headers instanceof Headers) {
    for (const [key, value] of headers.entries()) {
      out[key] = isSensitiveKey(key) ? REDACTED : sanitizeString(value);
    }
    return out;
  }

  for (const [key, value] of Object.entries(headers)) {
    if (isSensitiveKey(key)) {
      out[key] = REDACTED;
      continue;
    }

    if (Array.isArray(value)) {
      out[key] = value.map((item) => sanitizeString(String(item)));
      continue;
    }

    out[key] = value == null ? value : sanitizeString(String(value));
  }

  return out;
}

type SentryEventLike = {
  request?: unknown;
  extra?: unknown;
  contexts?: unknown;
  [key: string]: unknown;
};

export function sanitizeSentryEvent<T extends SentryEventLike>(event: T): T {
  const nextEvent = { ...event } as T;

  if (nextEvent.request) {
    nextEvent.request = sanitizeUnknown(nextEvent.request);
  }
  if (nextEvent.extra) {
    nextEvent.extra = sanitizeUnknown(nextEvent.extra);
  }
  if (nextEvent.contexts) {
    nextEvent.contexts = sanitizeUnknown(nextEvent.contexts);
  }

  return nextEvent;
}

export function sanitizeErrorMessage(message: string): string {
  return sanitizeString(message);
}
